import React from 'react';
import { XCircle, Receipt, Car } from 'lucide-react';

const OrderModal = ({
  order,
  onClose,
  setKotOrder,
  setShowKotView,
  getStatusColor,
  getStatusFlow,
  getStatusAction,
  updateOrderStatusMutation,
  onCancelOrder,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-semibold">Order #{order.id}</h2>
          {(() => {
            const kotReference =
              order.kot_reference || order.kotReference || order.kot?.reference;
            return (
              kotReference && (
                <button
                  onClick={() => {
                    setKotOrder(order);
                    setShowKotView(true);
                    onClose();
                  }}
                  className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  title="View KOT"
                >
                  <Receipt className="h-4 w-4" />
                  <span className="text-sm font-medium">View KOT</span>
                </button>
              )
            );
          })()}
        </div>
        <button
          onClick={onClose}
          className="text-secondary-400 hover:text-secondary-600"
        >
          <XCircle className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Order Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="font-medium text-secondary-900 mb-2">
              Order Information
            </h3>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Order ID:</span> #{order.id}
            </p>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">User ID:</span> {order.user_id}
            </p>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Order Type:</span>{' '}
              {order.order_type}
            </p>
            {order.delivery_instructions && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Delivery Instructions:</span>{' '}
                {order.delivery_instructions}
              </p>
            )}
            {order.allergy_note && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Allergy Note:</span>{' '}
                {order.allergy_note}
              </p>
            )}

            {/* Customer Information */}
            <div className="mt-3 pt-3 border-t border-secondary-200">
              <h4 className="font-medium text-secondary-900 mb-1">
                Customer
              </h4>
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Name:</span>{' '}
                {order.user?.name || '—'}
              </p>
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Phone:</span>{' '}
                {order.user?.phone_number ? (
                  <a
                    href={`tel:${order.user.phone_number}`}
                    className="text-primary-600 hover:underline"
                  >
                    {order.user.phone_number}
                  </a>
                ) : (
                  '—'
                )}
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 mb-2">
              Order Details
            </h3>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Total:</span> £{order.total_amount}
            </p>
            {order.subtotal !== undefined && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Subtotal:</span> £{order.subtotal}
              </p>
            )}
            {order.admin_fee > 0 && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Admin Fee:</span> £{order.admin_fee}
              </p>
            )}
            {order.service_fee > 0 && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Service Fee:</span> £{order.service_fee}
              </p>
            )}
            {order.tax > 0 && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Tax:</span> £{order.tax}
              </p>
            )}

            {order.scheduled_date && new Date(order.scheduled_date).getFullYear() > 1970 && (
              <p className="text-sm text-secondary-600">
                <span className="font-medium">Scheduled:</span> {new Date(order.scheduled_date).toLocaleDateString()} {order.scheduled_time}
              </p>
            )}

            <p className="text-sm text-secondary-600">
              <span className="font-medium">Payment Method:</span>
              <span
                className={`ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  order.payment_method === 'cash_at_counter'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {order.payment_method === 'cash_at_counter'
                  ? '💵 Cash at Counter'
                  : '💳 Card Payment'}
              </span>
            </p>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Payment Status:</span>
              <span
                className={`ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  order.payment_status === 'paid'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {order.payment_status}
              </span>
            </p>
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Status:</span>
              <span
                className={`ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status === 'pending_approval'
                  ? '⏳ Pending Approval'
                  : order.status}
              </span>
            </p>
            {(() => {
              const kotReference =
                order.kot_reference ||
                order.kotReference ||
                order.kot?.reference;
              return (
                kotReference && (
                  <p className="text-sm text-secondary-600">
                    <span className="font-medium">KOT Reference:</span>
                    <span className="ml-1 font-mono font-bold text-primary-600 bg-primary-50 px-2 py-1 rounded">
                      {kotReference}
                    </span>
                  </p>
                )
              );
            })()}
            <p className="text-sm text-secondary-600">
              <span className="font-medium">Created:</span>{' '}
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Vehicle Information */}
        {order.vehicle_data && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-3 flex items-center">
              <Car className="h-5 w-5 mr-2" />
              Vehicle Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <p className="text-xs font-medium text-blue-700 mb-1">Make</p>
                <p className="text-sm font-semibold text-blue-900">
                  {order.vehicle_data.make}
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <p className="text-xs font-medium text-blue-700 mb-1">Colour</p>
                <p className="text-sm font-semibold text-blue-900">
                  {order.vehicle_data.colour}
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <p className="text-xs font-medium text-blue-700 mb-1">
                  Registration
                </p>
                <p className="text-sm font-semibold text-blue-900 font-mono">
                  {order.vehicle_data.registrationNumber}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div>
          <h3 className="font-medium text-secondary-900 mb-2">
            Order Items ({order.orderItems?.length || 0})
          </h3>
          <div className="space-y-2">
            {order.orderItems && order.orderItems.length > 0 ? (
              order.orderItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="font-medium text-secondary-900">
                        {item.menuItem?.name || `Item #${item.menu_item_id}`}
                      </p>
                      {item.menuItem?.category_name && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.menuItem.category_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-secondary-600">
                      Quantity: {item.quantity} × £{item.price}
                    </p>
                    {item.menuItem?.description && (
                      <p className="text-sm text-secondary-500">
                        {item.menuItem.description}
                      </p>
                    )}

                    {/* Display addons if present */}
                    {item.addons && item.addons.length > 0 && (
                      <div className="mt-2 pl-4 border-l-2 border-primary-200">
                        <p className="text-xs font-medium text-primary-700 mb-1">
                          Add-ons:
                        </p>
                        {item.addons.map((addon, addonIndex) => (
                          <div
                            key={`${addon.id}-${addonIndex}`}
                            className="text-xs text-secondary-600 mb-1"
                          >
                            <span className="font-medium">
                              {addon.quantity}x
                            </span>
                            <span className="ml-1">
                              {addon.menuItemAddon?.name ||
                                `Addon #${addon.menu_item_addon_id}`}{' '}
                              - £{addon.price_at_time}
                            </span>
                            <span className="ml-2 text-primary-600 font-medium">
                              = £
                              {(
                                addon.quantity * parseFloat(addon.price_at_time)
                              ).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-secondary-900">
                      £
                      {item.total_price
                        ? parseFloat(item.total_price).toFixed(2)
                        : (item.quantity * parseFloat(item.price)).toFixed(2)}
                    </p>
                    {item.addons && item.addons.length > 0 && (
                      <p className="text-xs text-primary-600">
                        (incl. add-ons)
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-secondary-500">
                No items found for this order
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Status Flow */}
        <div>
          <h3 className="font-medium text-secondary-900 mb-3">
            Order Status Flow
          </h3>

          {/* Status Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-secondary-600">
                Progress
              </span>
              <span className="text-xs text-secondary-500">
                {(() => {
                  const flow = getStatusFlow(order);
                  const completedSteps = flow.flow.filter(
                    (_, index) => index <= flow.currentIndex
                  ).length;
                  const totalSteps = flow.flow.filter(
                    (s) => s.status !== 'cancelled'
                  ).length;
                  return `${completedSteps}/${totalSteps} steps`;
                })()}
              </span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${(() => {
                    const flow = getStatusFlow(order);
                    const completedSteps = flow.flow.filter(
                      (_, index) => index <= flow.currentIndex
                    ).length;
                    const totalSteps = flow.flow.filter(
                      (s) => s.status !== 'cancelled'
                    ).length;
                    return (completedSteps / totalSteps) * 100;
                  })()}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Status Flow Visualization */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            {getStatusFlow(order)
              .flow.filter((s) => s.status !== 'cancelled')
              .map((status, index) => {
                const flow = getStatusFlow(order);
                const isCurrent = status.status === order.status;
                const isCompleted = index < flow.currentIndex;
                const isNext = index === flow.nextIndex;

                return (
                  <div
                    key={status.status}
                    className={`p-2 rounded-lg text-center transition-all duration-200 ${
                      isCurrent
                        ? 'bg-primary-100 border-2 border-primary-500 shadow-md'
                        : isCompleted
                        ? 'bg-green-50 border border-green-200'
                        : isNext
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-secondary-50 border border-secondary-200'
                    }`}
                  >
                    <div className="text-lg mb-1">{status.icon}</div>
                    <div
                      className={`text-xs font-medium ${
                        isCurrent
                          ? 'text-primary-700'
                          : isCompleted
                          ? 'text-green-700'
                          : 'text-secondary-600'
                      }`}
                    >
                      {status.label}
                    </div>
                    {isCurrent && (
                      <div className="text-xs text-primary-600 font-bold mt-1">
                        CURRENT
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {(() => {
              const statusAction = getStatusAction(order);
              const flow = getStatusFlow(order);

              if (!statusAction) {
                return (
                  <div className="text-sm text-secondary-500 italic p-3 bg-secondary-50 rounded-lg">
                    ✅ Order completed - No further actions needed
                  </div>
                );
              }

              return (
                <>
                  <button
                    onClick={statusAction.action}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 text-white rounded-lg font-medium ${statusAction.color} disabled:opacity-50 transition-all duration-200 hover:shadow-lg`}
                    disabled={statusAction.loading}
                  >
                    {statusAction.loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <span>{statusAction.icon}</span>
                        <span>{statusAction.label}</span>
                      </>
                    )}
                  </button>

                  {flow.canCancel && (
                    <button
                      onClick={() => {
                        if (onCancelOrder) {
                          onCancelOrder(order.id);
                        }
                      }}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 transition-all duration-200 hover:shadow-lg"
                      disabled={updateOrderStatusMutation.isLoading}
                    >
                      <span>❌</span>
                      <span>Cancel Order</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          {/* Enhanced Cash at Counter Notice */}
          {order.status === 'pending_approval' &&
            order.payment_method === 'cash_at_counter' && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">💵</div>
                  <div>
                    <h4 className="font-semibold text-orange-900 mb-1">
                      Cash Payment Required
                    </h4>
                    <p className="text-sm text-orange-800">
                      This order requires cash payment verification before
                      processing. Please confirm the customer has paid the full
                      amount of <strong>£{order.total_amount}</strong> before
                      approving.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Status History */}
          <div className="mt-4">
            <h4 className="font-medium text-secondary-900 mb-2">
              Status History
            </h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-secondary-600">Order created</span>
                <span className="text-secondary-500 text-xs">
                  {new Date(order.created_at).toLocaleString()}
                </span>
              </div>
              {order.status !== 'pending' && (
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-secondary-600">
                    Status updated to {order.status}
                  </span>
                  <span className="text-secondary-500 text-xs">Just now</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default OrderModal;
